/**
 * admin/makua/AdminKitchen.js — Operations → Kitchen & Dining (V2.1, Page 8;
 * was "Kitchen").
 *
 *   Daily sheet   everyone eating a given meal: where they sleep, which
 *                 retreat, plated or buffet, every diet they ticked and their
 *                 allergy notes; searchable, sortable, printable, exportable
 *   Menus         the week's buffet and plated options (kitchen.edit)
 *   Diets         the list guests choose from (moved here from Earlier
 *                 Bookings → Catalog)
 *
 * Nothing medical is here, and nothing about money: only what the kitchen
 * needs to cook.
 */
import { useEffect, useMemo, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import toast from "react-hot-toast";
import AdminLayout from "../AdminLayout";
import { usePermissions, hasPermission } from "../permissions";
import { kitchenAPI, scheduledAPI } from "../../services/api";
import { useLoad, Field, fmtDate, fmtStamp, addDays, today, range, Badge } from "./ops";
import { useStored, useSort, SortTh, useReorder, ReorderHandle, useUnsaved } from "./kit";
import "./ops.css";

const MEALS = [["breakfast", "Breakfast"], ["lunch", "Lunch"], ["dinner", "Dinner"]];
const TABS = [["sheet", "Daily sheet"], ["menus", "Menus"], ["diets", "Diets"]];
const SHEET_DEFAULT = { service: "dinner", pathway_id: "", style: "", diet_id: "", q: "" };

export default function AdminKitchen() {
  const [params, setParams] = useSearchParams();
  const tab = TABS.some(([k]) => k === params.get("tab")) ? params.get("tab") : "sheet";
  const guardRef = useRef(null);
  const setTab = (k) => {
    const go = () => { const p = new URLSearchParams(params); if (k === "sheet") p.delete("tab"); else p.set("tab", k); setParams(p, { replace: true }); };
    if (guardRef.current && tab === "menus") guardRef.current(go)(); else go();
  };
  return (
    <AdminLayout pageTitle="Kitchen & Dining" pageSubtitle="Who's eating, what they can't eat, and what's on the menu." permission="kitchen.view" feature="reservations">
      <div className="ops">
        <div className="ops-tabs ops-noprint" role="tablist" aria-label="Kitchen & Dining">
          {TABS.map(([k, l]) => <button key={k} role="tab" aria-selected={tab === k} className={`ops-tab ${tab === k ? "is-on" : ""}`} onClick={() => setTab(k)}>{l}</button>)}
        </div>
        {tab === "sheet" && <Sheet />}
        {tab === "menus" && <Menus guardRef={guardRef} />}
        {tab === "diets" && <Diets />}
      </div>
    </AdminLayout>
  );
}

/* ══════════════════════ DAILY SHEET ══════════════════════ */
function Sheet() {
  const [date, setDate] = useState(today());
  const [saved, setSaved] = useStored("kitchen:sheet", SHEET_DEFAULT);
  const [f, setFRaw] = useState({ ...SHEET_DEFAULT, ...saved, q: "" });
  const setF = (x) => { setFRaw(x); setSaved({ ...x, q: "" }); };
  const [debouncedQ, setDebouncedQ] = useState("");
  useEffect(() => { const t = setTimeout(() => setDebouncedQ(f.q), 250); return () => clearTimeout(t); }, [f.q]);
  const query = { ...f, q: debouncedQ.trim(), date };
  const { data: dates } = useLoad(() => scheduledAPI.list({ scope: "all" }), []);
  const { data: diets } = useLoad(() => kitchenAPI.diets(), []);
  const { data, loading, error } = useLoad(() => kitchenAPI.report(query), [JSON.stringify(query)]);
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });
  const s = data?.summary;
  const meal = MEALS.find(([k]) => k === f.service)[1];
  const filtersOn = f.pathway_id || f.style || f.diet_id || f.q;
  const accessors = useMemo(() => ({
    guest: (g) => g.guest, room: (g) => g.room, retreat: (g) => g.retreat, style: (g) => g.style, diets: (g) => g.diets || "~", notes: (g) => (g.notes ? 0 : 1), status: (g) => g.status,
  }), []);
  const { rows, sort, setSort } = useSort(data?.rows || [], accessors, null, "kitchen:sort");

  return (
    <>
      <div className="ops-bar ops-toolbar">
        <div className="ops-field"><span>Date</span>
          <div className="ops-actions" style={{ justifyContent: "flex-start", flexWrap: "nowrap" }}>
            <button className="ops-btn ops-btn--sm" aria-label="Previous day" onClick={() => setDate(addDays(date, -1))}>‹</button>
            <input type="date" aria-label="Date" value={date} onChange={(e) => e.target.value && setDate(e.target.value)} />
            <button className="ops-btn ops-btn--sm" aria-label="Next day" onClick={() => setDate(addDays(date, 1))}>›</button>
            {date !== today() && <button className="ops-btn ops-btn--sm" onClick={() => setDate(today())}>Today</button>}
          </div>
        </div>
        <div className="ops-field"><span>Meal</span>
          <div className="ops-seg" role="group" aria-label="Meal">
            {MEALS.map(([k, l]) => <button key={k} type="button" aria-pressed={f.service === k} onClick={() => setF({ ...f, service: k })}>{l}</button>)}
          </div>
        </div>
        <Field label="Retreat">
          <select value={f.pathway_id} onChange={set("pathway_id")}><option value="">Everyone in house</option>
            {(dates || []).filter((d) => d.start_date <= addDays(date, 14) && d.end_date >= addDays(date, -14)).map((d) => <option key={d.id} value={d.id}>{d.name} · {range(d.start_date, d.end_date)}</option>)}
          </select>
        </Field>
        <Field label="Service"><select value={f.style} onChange={set("style")}><option value="">Buffet and plated</option><option value="buffet">Buffet</option><option value="plated">Plated</option></select></Field>
        <Field label="Diet"><select value={f.diet_id} onChange={set("diet_id")}><option value="">Any</option>{(diets || []).filter((d) => d.active).map((d) => <option key={d.id} value={d.id}>{d.name}</option>)}</select></Field>
        <Field label="Search" className="ops-search"><input type="search" value={f.q} onChange={set("q")} placeholder="Guest, room or MAK-…" /></Field>
        {filtersOn && <button className="ops-btn ops-btn--sm" style={{ alignSelf: "flex-end" }} onClick={() => setF({ ...SHEET_DEFAULT, service: f.service })}>Clear filters</button>}
        <span className="ops-spacer" />
        <button className="ops-btn" onClick={() => window.print()}>Print</button>
        <a className="ops-btn" href={kitchenAPI.csv(query)}>Export CSV</a>
      </div>

      <div className="ops-print-head">
        <h2 style={{ fontSize: "1.1rem", margin: "0.25rem 0 0" }}>{meal}, {fmtDate(date, { weekday: "long", day: "numeric", month: "long", year: "numeric" })}</h2>
        {s && <p className="ops-muted" style={{ margin: "0.2rem 0 0" }}>
          {s.total} meal{s.total === 1 ? "" : "s"} planned: {s.confirmed} confirmed, {s.held} held (not yet paid){filtersOn ? " · filtered" : ""} · prepared {fmtStamp(data.generated_at)}
        </p>}
      </div>
      {error && <p className="ops-error">{error}</p>}
      {s && (
        <div className="ops-stats">
          <div className="ops-stat"><b>{s.total}</b><span>eating</span></div>
          <div className="ops-stat"><b>{s.confirmed}</b><span>confirmed</span></div>
          <div className="ops-stat"><b>{s.held}</b><span>held, may change</span></div>
          <div className="ops-stat"><b>{s.byStyle.plated || 0}</b><span>plated</span></div>
          <div className="ops-stat"><b>{s.byStyle.buffet || 0}</b><span>buffet</span></div>
          <div className="ops-stat"><b>{s.withNotes}</b><span>with allergies or notes</span></div>
          {Object.entries(s.byDiet).sort((a, b) => b[1] - a[1]).map(([d, n]) => <div className="ops-stat" key={d}><b>{n}</b><span>{d}</span></div>)}
        </div>
      )}
      {data?.menu && (
        <div className="ops-card">
          <h3>On the menu</h3>
          {data.menu.buffet && <p style={{ margin: "0 0 0.4rem", whiteSpace: "pre-line" }}><b>Buffet:</b> {data.menu.buffet}</p>}
          {data.menu.plated_options && <p style={{ margin: 0, whiteSpace: "pre-line" }}><b>Plated options:</b> {data.menu.plated_options}</p>}
          {data.menu.notes && <p className="ops-muted" style={{ margin: "0.4rem 0 0" }}><b>Chef's choice and notes:</b> {data.menu.notes}</p>}
        </div>
      )}
      <div className="ops-table-wrap ops-table-wrap--sticky">
        <table className="ops-table">
          <thead><tr>
            <SortTh k="guest" sort={sort} setSort={setSort}>Guest</SortTh>
            <SortTh k="room" sort={sort} setSort={setSort}>Room / bed</SortTh>
            <SortTh k="retreat" sort={sort} setSort={setSort}>Retreat</SortTh>
            <SortTh k="style" sort={sort} setSort={setSort}>Service</SortTh>
            <SortTh k="diets" sort={sort} setSort={setSort}>Diets</SortTh>
            <SortTh k="notes" sort={sort} setSort={setSort}>Allergies and notes</SortTh>
            <th>Menu choice</th>
          </tr></thead>
          <tbody>
            {loading && !data && <tr><td colSpan={7} className="ops-empty">Loading…</td></tr>}
            {data && !rows.length && <tr><td colSpan={7} className="ops-empty">{filtersOn ? "No one matches these filters for this meal." : "No one is in house for this meal."}</td></tr>}
            {rows.map((g, i) => (
              <tr key={`${g.ref}-${i}`}>
                <td><b>{g.guest}</b><span className="ops-sub">{g.ref}{g.status === "pending_payment" ? " · held, not yet paid" : ""}</span></td>
                <td>{g.room}</td>
                <td>{g.retreat}</td>
                <td>{g.style === "plated" ? "Plated" : "Buffet"}</td>
                <td>{g.diets || "—"}</td>
                <td style={g.notes ? { color: "var(--admin-red)", fontWeight: 600 } : null}>{g.notes || "—"}</td>
                <td>{g.choice || (g.style === "plated" ? "Chef's choice" : "—")}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <p className="ops-muted ops-noprint">Breakfast lists everyone who slept here the night before; lunch and dinner list everyone sleeping here tonight. Each person is counted once. Held reservations are included (and counted separately) so the kitchen isn't caught short.</p>
    </>
  );
}

/* ══════════════════════ MENUS ══════════════════════ */
function Menus({ guardRef }) {
  const { admin } = usePermissions();
  const canEdit = hasPermission(admin, "kitchen.edit");
  const [from, setFrom] = useState(today());
  const to = addDays(from, 6);
  const { data, reload } = useLoad(() => kitchenAPI.menus(from, to), [from]);
  const days = Array.from({ length: 7 }, (_, i) => addDays(from, i));
  const [openDay, setOpenDay] = useState(today());
  const [dirtyCells, setDirtyCells] = useState({});
  const dirty = Object.values(dirtyCells).some(Boolean);
  const guard = useUnsaved(dirty);
  useEffect(() => { guardRef.current = guard; return () => { guardRef.current = null; }; }, [guard, guardRef]);
  const menuFor = (d, s) => (data?.menus || []).find((m) => m.menu_date === d && m.service === s) || {};
  const markDirty = (key, v) => setDirtyCells((c) => (c[key] === v ? c : { ...c, [key]: v }));
  const week = (n) => guard(() => { setDirtyCells({}); setFrom(addDays(from, 7 * n)); setOpenDay(addDays(from, 7 * n)); })();

  return (
    <>
      <div className="ops-bar ops-toolbar">
        <button className="ops-btn" onClick={() => week(-1)} aria-label="Previous week">‹</button>
        <Field label="Week from"><input type="date" value={from} onChange={(e) => e.target.value && guard(() => { setDirtyCells({}); setFrom(e.target.value); setOpenDay(e.target.value); })()} /></Field>
        <button className="ops-btn" onClick={() => week(1)} aria-label="Next week">›</button>
        {dirty && <span className="ops-dirty">Unsaved changes</span>}
        <p className="ops-muted" style={{ margin: 0, alignSelf: "center", maxWidth: "36rem" }}>
          Plated options are offered to guests on plated service; anyone who doesn't choose gets the chef's choice.{canEdit ? "" : " You can read menus; changing them needs the kitchen edit permission."}
        </p>
      </div>
      <div className="ops-daypick" role="tablist" aria-label="Day">
        {days.map((d) => {
          const has = MEALS.some(([k]) => { const m = menuFor(d, k); return m.buffet || m.plated_options; });
          return (
            <button key={d} type="button" role="tab" aria-selected={openDay === d} className={openDay === d ? "is-on" : ""} onClick={() => setOpenDay(d)}>
              <span>{fmtDate(d, { weekday: "short" })}</span><b>{fmtDate(d, { day: "numeric" })}</b>
              <small>{has ? "menu set" : "no menu"}</small>
            </button>
          );
        })}
      </div>
      {days.filter((d) => d === openDay).map((d) => (
        <div className="ops-card" key={d}>
          <h3>{fmtDate(d, { weekday: "long", day: "numeric", month: "long" })}</h3>
          <div className="ops-grid ops-grid--3">
            {MEALS.map(([k, l]) => (
              <MenuCell key={`${d}${k}${menuFor(d, k).updated_at || ""}`} date={d} service={k} label={l} m={menuFor(d, k)} canEdit={canEdit}
                onDirty={(v) => markDirty(`${d}${k}`, v)} onSaved={() => { markDirty(`${d}${k}`, false); reload(); }} />
            ))}
          </div>
        </div>
      ))}
    </>
  );
}

function MenuCell({ date, service, label, m, canEdit, onSaved, onDirty }) {
  const [f, setF] = useState({ buffet: m.buffet || "", plated_options: m.plated_options || "", notes: m.notes || "" });
  const dirty = f.buffet !== (m.buffet || "") || f.plated_options !== (m.plated_options || "") || f.notes !== (m.notes || "");
  useEffect(() => { onDirty(dirty); }, [dirty]); // eslint-disable-line react-hooks/exhaustive-deps
  const save = async () => {
    try { await kitchenAPI.saveMenu({ menu_date: date, service, ...f }); toast.success(`${label} saved for ${fmtDate(date, { weekday: "long" })}.`); onSaved(); } catch (err) { toast.error(err.message); }
  };
  return (
    <fieldset className="ops-menu-cell" disabled={!canEdit}>
      <legend>{label}</legend>
      <Field label="Buffet"><textarea rows={3} value={f.buffet} onChange={(e) => setF({ ...f, buffet: e.target.value })} /></Field>
      <Field label="Plated options, one per line"><textarea rows={3} value={f.plated_options} onChange={(e) => setF({ ...f, plated_options: e.target.value })} /></Field>
      <Field label="Chef's choice and notes"><input value={f.notes} onChange={(e) => setF({ ...f, notes: e.target.value })} placeholder="Chef's choice: …" /></Field>
      {canEdit && (
        <div className="ops-actions" style={{ justifyContent: "flex-start" }}>
          <button type="button" className="ops-btn ops-btn--sm ops-btn--primary" disabled={!dirty} onClick={save}>Save {label.toLowerCase()}</button>
          {dirty && <button type="button" className="ops-btn ops-btn--sm" onClick={() => setF({ buffet: m.buffet || "", plated_options: m.plated_options || "", notes: m.notes || "" })}>Undo</button>}
        </div>
      )}
    </fieldset>
  );
}

/* ══════════════════════ DIETS ══════════════════════ */
function Diets() {
  const { admin } = usePermissions();
  const canEdit = hasPermission(admin, "kitchen.edit");
  const { data, error, reload } = useLoad(() => kitchenAPI.diets(), []);
  const [name, setName] = useState("");
  const rows = data || [];
  const run = async (fn, ok) => { try { const r = await fn(); toast.success(r?.message || ok); reload(); return true; } catch (err) { toast.error(err.message); return false; } };
  const order = useReorder(rows, async (ids) => {
    for (let i = 0; i < ids.length; i++) {
      const d = rows.find((x) => x.id === ids[i]);
      if (d && d.sort_order !== i) await kitchenAPI.saveDiet({ name: d.name, sort_order: i, active: !!d.active }, d.id);
    }
    toast.success("Order saved."); reload();
  });
  return (
    <>
      <p className="ops-muted" style={{ margin: 0 }}>What guests can tick when they book, and what this sheet counts. A diet someone has chosen is hidden rather than deleted, so their records keep it.</p>
      {error && <p className="ops-error">{error}</p>}
      <div className="ops-table-wrap">
        <table className="ops-table">
          <thead><tr>{canEdit && <th><span className="ops-visually-hidden">Order</span></th>}<th>Diet</th><th>On the booking form</th><th /></tr></thead>
          <tbody>
            {!rows.length && <tr><td colSpan={4} className="ops-empty">No diets yet.</td></tr>}
            {(canEdit ? order.items : rows).map((d) => (
              <tr key={d.id} {...(canEdit ? order.rowProps(d.id) : {})} style={d.active ? null : { opacity: 0.55 }}>
                {canEdit && <td><ReorderHandle r={order} id={d.id} label={d.name} /></td>}
                <td><b>{d.name}</b>{d.is_exclusive ? <span className="ops-sub">can't be combined with others</span> : null}</td>
                <td>{d.active ? <Badge tone="green">Offered</Badge> : <Badge>Hidden</Badge>}</td>
                <td className="num">
                  {canEdit && <>
                    <button className="ops-btn ops-btn--sm" onClick={() => { const n = window.prompt("Diet name", d.name); if (n && n.trim() && n !== d.name) run(() => kitchenAPI.saveDiet({ name: n.trim(), sort_order: d.sort_order, active: !!d.active }, d.id), "Renamed."); }}>Rename</button>{" "}
                    <button className="ops-btn ops-btn--sm" onClick={() => run(() => kitchenAPI.saveDiet({ name: d.name, sort_order: d.sort_order, active: !d.active }, d.id), d.active ? "Hidden." : "Offered.")}>{d.active ? "Hide" : "Offer"}</button>{" "}
                    <button className="ops-btn ops-btn--sm ops-btn--danger" onClick={() => window.confirm(`Delete ${d.name}? If anyone has chosen it, it's hidden instead.`) && run(() => kitchenAPI.removeDiet(d.id), "Deleted.")}>Delete</button>
                  </>}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {canEdit && (
        <form className="ops-bar ops-toolbar" onSubmit={async (e) => { e.preventDefault(); if (await run(() => kitchenAPI.saveDiet({ name: name.trim(), sort_order: rows.length }), "Added.")) setName(""); }}>
          <Field label="New diet"><input value={name} onChange={(e) => setName(e.target.value)} placeholder="Low FODMAP" /></Field>
          <button className="ops-btn ops-btn--primary" style={{ alignSelf: "flex-end" }} disabled={!name.trim()}>Add diet</button>
        </form>
      )}
    </>
  );
}
